/**
 * Dialog centralisé pour la création et l'édition de chantiers.
 * Utilisé par Projects.tsx, Dashboard.tsx et ProjectDetail.tsx.
 *
 * - Création : affiche le stepper 3 étapes (ChantierCreationStepper)
 * - Édition : affiche le formulaire plat (ChantierEditForm)
 * - DayPeriodSelector intégré (demi-journées et différentiel jours ouvrés)
 * - Validation Zod, toasts Sonner, soumission Supabase
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast-utils";
import { toasts } from "@/lib/content";
import { calculerJoursOuvres } from "@/lib/heures-utils";
import { getStatutsDisponiblesCreation } from "@/constants/statuts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DayPeriodSelector, {
  convertWorkDaysToJsonb,
  convertJsonbToWorkDays,
  generateAutoSelection,
  SelectedWorkDaysMap,
  calculateTotalSelectedDays,
} from "@/components/ui/DayPeriodSelector";
import { ChantierCreationStepper } from "@/components/chantiers/ChantierCreationStepper";
import { ChantierEditForm } from "@/components/chantiers/ChantierEditForm";
import type { JourDetail } from "@/types/calendar";
import { useModalProtection } from '@/hooks/useModalProtection';

// Schema de validation Zod pour creation/edition de chantier
const chantierFormSchema = z.object({
  nom_chantier: z.string().min(1, "Le nom du chantier est requis").max(150, "150 caractères max"),
  reference_chantier: z.string().min(1, "La référence est requise").max(50, "50 caractères max"),
  client: z.string().min(1, "Le client est requis").max(150, "150 caractères max"),
  adresse: z.string().min(1, "L'adresse est requise").max(300, "300 caractères max"),
  duree_estimee_jours: z.number().min(0.5, "Durée minimum 0.5 jour").max(3650, "Durée max 10 ans"),
  description: z.string().max(2000, "2000 caractères max").optional(),
  etat_chantier: z.string().min(1, "L'état est requis"),
  date_debut_prevue: z.string().min(1, "La date de début est requise"),
  date_fin_estimee: z.string().optional(),
  date_fin_reelle: z.string().optional(),
  pictogramme: z.string().optional(),
  couleur: z.string().optional(),
});

interface ChantierFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Projet à éditer, ou null/undefined pour le mode création */
  project?: any | null;
  /** Callback après succès — reçoit l'ID du chantier créé (ou undefined en édition) */
  onSuccess: (chantierId?: string) => void;
  entrepriseId: string | null;
  /** Afficher le champ Budget HT dans le formulaire d'édition (pour ProjectDetail) */
  showBudgetField?: boolean;
}

const EMPTY_FORM = {
  nom_chantier: "",
  reference_chantier: "",
  client: "",
  adresse: "",
  duree_estimee_jours: 30,
  budget_ht: 0,
  description: "",
  etat_chantier: "brouillon",
  date_debut_prevue: new Date().toISOString().split('T')[0],
  date_fin_estimee: "",
  date_fin_reelle: "",
  pictogramme: "Home",
  couleur: "#2563eb",
  jours_details: null as JourDetail[] | null,
};

export function ChantierFormDialog({
  open,
  onOpenChange,
  project,
  onSuccess,
  entrepriseId,
  showBudgetField = false,
}: ChantierFormDialogProps) {
  useModalProtection(open);

  // --- État du formulaire ---
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(false);
  const [dureeManuelleModifiee, setDureeManuelleModifiee] = useState(false);

  // --- État DayPeriodSelector ---
  const [showDayPeriodModal, setShowDayPeriodModal] = useState(false);
  const [selectedWorkDays, setSelectedWorkDays] = useState<SelectedWorkDaysMap>(new Map());
  const [hasConfirmedDaySelection, setHasConfirmedDaySelection] = useState(false);
  const isUpdatingFromModalRef = useRef(false);

  // Mode édition ou création
  const isEditing = !!project;

  // Statuts disponibles selon les dates saisies
  const statutsDisponibles = useMemo(
    () => getStatutsDisponiblesCreation(formData.date_debut_prevue, formData.date_fin_estimee),
    [formData.date_debut_prevue, formData.date_fin_estimee]
  );

  // --- Initialisation / Reset ---
  useEffect(() => {
    if (open && project) {
      // Mode édition : pré-remplir depuis le projet
      setDureeManuelleModifiee(true);
      setFormData({
        nom_chantier: project.nom_chantier || "",
        reference_chantier: project.reference_chantier || "",
        client: project.client || "",
        adresse: project.adresse || "",
        duree_estimee_jours: project.duree_estimee_jours || project.duree_estimee || 30,
        budget_ht: project.budget_ht || 0,
        description: project.description || "",
        etat_chantier: project.etat_chantier || "brouillon",
        date_debut_prevue: project.date_debut_prevue || new Date().toISOString().split('T')[0],
        date_fin_estimee: project.date_fin_estimee || "",
        date_fin_reelle: project.date_fin_reelle || "",
        pictogramme: project.pictogramme || "",
        couleur: project.couleur || "",
        jours_details: project.jours_details || null,
      });
      // Charger jours_details existants
      if (project.jours_details && project.jours_details.length > 0) {
        setSelectedWorkDays(convertJsonbToWorkDays(project.jours_details));
        setHasConfirmedDaySelection(true);
      } else {
        setSelectedWorkDays(new Map());
        setHasConfirmedDaySelection(false);
      }
    } else if (open && !project) {
      // Mode création : reset
      setFormData({ ...EMPTY_FORM, date_debut_prevue: new Date().toISOString().split('T')[0] });
      setDureeManuelleModifiee(false);
      setSelectedWorkDays(new Map());
      setHasConfirmedDaySelection(false);
      isUpdatingFromModalRef.current = false;
    }
  }, [open, project]);

  // Reset complet à la fermeture
  useEffect(() => {
    if (!open) {
      setShowDayPeriodModal(false);
      setLoading(false);
    }
  }, [open]);

  // --- Calcul automatique jours ouvrés quand les dates changent ---
  useEffect(() => {
    if (formData.date_debut_prevue && formData.date_fin_estimee && !dureeManuelleModifiee) {
      const jours = calculerJoursOuvres(formData.date_debut_prevue, formData.date_fin_estimee);
      if (jours > 0) {
        setFormData(prev => ({ ...prev, duree_estimee_jours: jours }));
      }
    }
  }, [formData.date_debut_prevue, formData.date_fin_estimee, dureeManuelleModifiee]);

  // --- Détection ouverture DayPeriodSelector ---
  useEffect(() => {
    if (isUpdatingFromModalRef.current) {
      isUpdatingFromModalRef.current = false;
      return;
    }
    if (!formData.date_debut_prevue || !formData.date_fin_estimee || !dureeManuelleModifiee) return;

    const joursOuvresCalcules = calculerJoursOuvres(formData.date_debut_prevue, formData.date_fin_estimee);
    const dureeSaisie = formData.duree_estimee_jours;
    const hasDecimal = dureeSaisie % 1 !== 0;
    const differenceJours = dureeSaisie !== joursOuvresCalcules;

    if (hasDecimal || differenceJours) {
      const autoSelection = generateAutoSelection(
        new Date(formData.date_debut_prevue),
        new Date(formData.date_fin_estimee),
        dureeSaisie
      );
      setSelectedWorkDays(autoSelection);
      setHasConfirmedDaySelection(false);
      // Différer l'ouverture pour éviter le conflit de focus trap entre Dialog et AlertDialog Radix
      setTimeout(() => {
        setShowDayPeriodModal(true);
      }, 50);
    }
  }, [formData.duree_estimee_jours, formData.date_debut_prevue, formData.date_fin_estimee, dureeManuelleModifiee]);

  // --- Helper pour mettre à jour un champ ---
  const updateField = useCallback((field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // --- Soumission du formulaire ---
  const submitForm = async () => {
    // Validation Zod
    const validation = chantierFormSchema.safeParse({
      ...formData,
      description: formData.description || undefined,
      date_fin_estimee: formData.date_fin_estimee || undefined,
      date_fin_reelle: formData.date_fin_reelle || undefined,
      pictogramme: formData.pictogramme || undefined,
      couleur: formData.couleur || undefined,
    });

    if (!validation.success) {
      validation.error.issues.forEach(issue => {
        toast.error("Erreur de validation", { description: issue.message });
      });
      return;
    }

    setLoading(true);

    try {
      // Convertir selectedWorkDays en JSONB si confirmé
      const joursDetails = hasConfirmedDaySelection && selectedWorkDays.size > 0
        ? convertWorkDaysToJsonb(selectedWorkDays)
        : null;

      // Exclure jours_details et budget_ht du formData pour le nettoyage
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { jours_details: _, budget_ht: _budget, ...formDataBase } = formData;

      const cleanedData: Record<string, any> = {
        ...formDataBase,
        date_fin_estimee: formData.date_fin_estimee || null,
        date_fin_reelle: formData.date_fin_reelle || null,
        entreprise_id: entrepriseId,
      };

      // Ajouter jours_details si confirmé
      if (joursDetails !== null) {
        cleanedData.jours_details = joursDetails;
      }

      // Ajouter budget_ht en édition si le champ est visible
      if (isEditing && showBudgetField) {
        cleanedData.budget_ht = formData.budget_ht || 0;
      }

      if (isEditing) {
        const { error } = await supabase
          .from("chantiers")
          .update(cleanedData)
          .eq("id", project.id);

        if (error) throw error;

        toast.success(toasts.updated, { description: "Le chantier a été modifié avec succès." });
        onOpenChange(false);
        onSuccess();
      } else {
        const { data: newChantier, error } = await supabase
          .from("chantiers")
          .insert(cleanedData)
          .select('id')
          .single();

        if (error) throw error;

        toast.success(toasts.created, { description: "Le nouveau chantier a été ajouté avec succès." });
        onOpenChange(false);
        onSuccess(newChantier?.id);
      }
    } catch (error: any) {
      console.error("Erreur chantier:", error);
      toast.error("Erreur", { description: error.message || toasts.errorGeneric });
    } finally {
      setLoading(false);
    }
  };

  // Wrapper pour le mode édition (form HTML natif avec Enter = submit)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitForm();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          {isEditing ? (
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle className="text-2xl font-black">Modifier le chantier</DialogTitle>
                <DialogDescription className="text-base">Modifiez les informations du chantier</DialogDescription>
              </DialogHeader>
              <ChantierEditForm
                formData={formData}
                updateField={updateField}
                setDureeManuelleModifiee={setDureeManuelleModifiee}
                statutsDisponibles={statutsDisponibles}
                showBudgetField={showBudgetField}
              />
              <DialogFooter>
                <Button type="submit" disabled={loading} size="lg" className="font-bold">
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Enregistrer les modifications
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-black">Créer un nouveau chantier</DialogTitle>
                <DialogDescription className="text-base">Renseignez les informations du chantier</DialogDescription>
              </DialogHeader>
              <ChantierCreationStepper
                formData={formData}
                setFormData={(updater) => setFormData(updater)}
                setDureeManuelleModifiee={setDureeManuelleModifiee}
                statutsDisponibles={statutsDisponibles}
                loading={loading}
                onCancel={() => onOpenChange(false)}
                onSubmit={submitForm}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* AlertDialog DayPeriodSelector — en sibling du Dialog pour éviter le conflit focus trap Radix */}
      <AlertDialog open={showDayPeriodModal} onOpenChange={setShowDayPeriodModal}>
        <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Sélection des jours travaillés</AlertDialogTitle>
            <AlertDialogDescription>
              La durée saisie ({formData.duree_estimee_jours} jours) diffère des jours ouvrés calculés.
              Sélectionnez précisément les jours et demi-journées qui seront travaillés.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {formData.date_debut_prevue && formData.date_fin_estimee && (
            <DayPeriodSelector
              dateDebut={new Date(formData.date_debut_prevue)}
              dateFin={new Date(formData.date_fin_estimee)}
              selectedDays={selectedWorkDays}
              onChange={setSelectedWorkDays}
              expectedTotalDays={formData.duree_estimee_jours}
            />
          )}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDayPeriodModal(false);
              // Réinitialiser la durée au calcul automatique
              if (formData.date_debut_prevue && formData.date_fin_estimee) {
                const jours = calculerJoursOuvres(formData.date_debut_prevue, formData.date_fin_estimee);
                setFormData(prev => ({ ...prev, duree_estimee_jours: jours }));
              }
              setDureeManuelleModifiee(false);
              setSelectedWorkDays(new Map());
            }}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              const totalSelected = calculateTotalSelectedDays(selectedWorkDays);

              // Trouver la date du dernier jour sélectionné pour adapter date_fin_estimee
              let lastSelectedDate: string | null = null;
              selectedWorkDays.forEach((_, key) => {
                const dateStr = key.split(':')[0];
                if (!lastSelectedDate || dateStr > lastSelectedDate) {
                  lastSelectedDate = dateStr;
                }
              });

              // Activer le flag (ref) pour éviter la réouverture du modal
              isUpdatingFromModalRef.current = true;

              setFormData(prev => ({
                ...prev,
                duree_estimee_jours: totalSelected,
                date_fin_estimee: lastSelectedDate && lastSelectedDate > prev.date_fin_estimee
                  ? lastSelectedDate
                  : prev.date_fin_estimee,
              }));
              setHasConfirmedDaySelection(true);
              setShowDayPeriodModal(false);
            }}>
              Confirmer ({calculateTotalSelectedDays(selectedWorkDays)} jours)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
